import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

interface AuthState {
  isLoggedIn: boolean;
  isLoading: boolean;
  userName: string | null;
  accessToken: string | null;
  checkAuthState: () => Promise<void>;
}

export const useAuthState = (): AuthState => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userName, setUserName] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const checkAuthState = useCallback(async () => {
    try {
      setIsLoading(true);
      const user = await getCurrentUser();

      if (user) {
        console.log('useAuthState: User is authenticated:', user.username);
        setIsLoggedIn(true);
        setUserName(user.username || null);

        try {
          const authSession = await fetchAuthSession();
          const token = authSession.tokens?.accessToken?.toString();
          setAccessToken(token || null);
        } catch (sessionError) {
          console.warn('useAuthState: Could not get session:', sessionError);
          setAccessToken(null);
        }
      } else {
        console.log('useAuthState: No authenticated user');
        setIsLoggedIn(false);
        setUserName(null);
        setAccessToken(null);
      }
    } catch (error) {
      // Handle the common case where user is not authenticated
      if (
        error instanceof Error &&
        (error.name === 'UserUnAuthenticatedException' ||
          error.message.includes('User needs to be authenticated'))
      ) {
        console.log(
          'useAuthState: User not authenticated (normal for logged-out state)',
        );
      } else {
        console.log('useAuthState: Authentication check failed:', error);
      }
      setIsLoggedIn(false);
      setUserName(null);
      setAccessToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth state on mount
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  return {
    isLoggedIn,
    isLoading,
    userName,
    accessToken,
    checkAuthState,
  };
};
